/**
 * Backend Health Tracker
 *
 * Tracks backend failures and determines when to enter maintenance mode.
 * Uses sliding window approach - only triggers maintenance if multiple
 * failures occur within a short time window.
 *
 * Strategy:
 * - Track failures with timestamps
 * - If 3+ failures within 30 seconds, perform final ping check
 * - Only enter maintenance if final ping confirms backend is down
 * - Reset tracking on any successful request
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

interface FailureRecord {
  timestamp: number;
  endpoint: string;
}

class BackendHealthTracker {
  private failures: FailureRecord[] = [];
  private readonly FAILURE_THRESHOLD = 3;
  private readonly TIME_WINDOW_MS = 30000; // 30 seconds
  private isCheckingMaintenance = false;

  /**
   * Record a backend failure
   */
  recordFailure(endpoint: string): void {
    const now = Date.now();

    // Add new failure
    this.failures.push({
      timestamp: now,
      endpoint,
    });

    // Clean up old failures outside time window
    this.failures = this.failures.filter(
      (f) => now - f.timestamp < this.TIME_WINDOW_MS,
    );

    console.warn("Backend failure recorded:", {
      endpoint,
      totalFailures: this.failures.length,
      threshold: this.FAILURE_THRESHOLD,
    });
  }

  /**
   * Check if we should perform maintenance check
   * Returns true if failure threshold exceeded
   */
  shouldCheckMaintenance(): boolean {
    const recentFailures = this.getRecentFailures();
    return (
      recentFailures >= this.FAILURE_THRESHOLD && !this.isCheckingMaintenance
    );
  }

  /**
   * Perform final ping check to confirm backend is actually down
   * @returns true if backend is healthy, false if down
   */
  async performFinalPingCheck(): Promise<boolean> {
    if (this.isCheckingMaintenance) {
      return true; // Already checking, assume healthy to avoid recursion
    }

    this.isCheckingMaintenance = true;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(`${API_BASE_URL}/api/v1/ping`, {
        method: "GET",
        signal: controller.signal,
        cache: "no-store",
      });

      clearTimeout(timeoutId);

      const isHealthy = response.ok && response.status !== 503;

      if (isHealthy) {
        // Backend is actually healthy, reset failures
        this.reset();
      }

      return isHealthy;
    } catch (error) {
      console.error("Final ping check failed:", error);
      return false;
    } finally {
      this.isCheckingMaintenance = false;
    }
  }

  /**
   * Get count of recent failures within time window
   */
  private getRecentFailures(): number {
    const now = Date.now();
    return this.failures.filter((f) => now - f.timestamp < this.TIME_WINDOW_MS)
      .length;
  }

  /**
   * Reset failure tracking (call on successful requests)
   */
  reset(): void {
    this.failures = [];
  }

  /**
   * Get current failure count for debugging
   */
  getFailureCount(): number {
    return this.getRecentFailures();
  }
}

// Singleton instance
export const backendHealthTracker = new BackendHealthTracker();
